import {useCallback, useContext, useEffect, useRef, useState} from "react";
import {useLocation} from "react-router-dom";
import {ProjectsContext} from "../../App";
import Bio from "../../sections/Bio/Bio";
import Contact from "../../sections/Contact/Contact";
import Gallery from "../../components/Gallery/Gallery";
import IntroVideo from "../../components/IntroVideo/IntroVideo";
import {loadDataWithCache, sortByOrderMetaData} from "../../utils";
import './style.scss';

const sections = [
    {key: 'cinematography', label: 'Cinematography', path: '/cinematography'},
    {key: 'dance', label: 'Dance', path: '/dance'},
    {key: 'exhibition', label: 'Exhibitions', path: '/exhibition/project/exhibition'},
    {key: 'directing', label: 'Directing', path: '/directing'},
    {key: 'private-projects', dataKey: 'privet-projects', label: 'Private Projects', path: '/private-projects'},
];

const Home = () => {
    const location = useLocation();
    const {laurels, imagesCache, setImagesCache} = useContext(ProjectsContext);
    const [galleries, setGalleries] = useState({});
    const firstHeadingRef = useRef(null);

    useEffect(() => {
        const heading = firstHeadingRef.current;
        if (!heading) return undefined;
        const nav = document.querySelector('.nav');
        if (!nav) return undefined;

        const updateNavBackground = () => {
            const headingTop = heading.getBoundingClientRect().top;
            const navBottom = nav.getBoundingClientRect().bottom;
            nav.classList.toggle('nav-background', headingTop <= navBottom);
        };

        updateNavBackground();
        window.addEventListener('scroll', updateNavBackground, {passive: true});

        return () => {
            window.removeEventListener('scroll', updateNavBackground);
            nav.classList.remove('nav-background');
        };
    }, []);

    useEffect(() => {
        const anchor = location.hash.slice(1);
        if (!anchor) return undefined;

        const scrollToAnchor = () => {
            document.getElementById(anchor)?.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
            });
        };

        const frame = window.requestAnimationFrame(scrollToAnchor);
        return () => window.cancelAnimationFrame(frame);
    }, [location.hash]);

    const getData = useCallback(async () => {
        const loadedSections = await Promise.all(sections.map(async section => {
            const images = await loadDataWithCache(`${section.dataKey || section.key}-main`, imagesCache, setImagesCache, section.key !== 'exhibition' ? 'image' : 'video');
            const withLaurels = section.key === 'cinematography' || section.key === 'directing'
                ? images.map(image => {
                    const projectName = image?.context?.custom?.name;
                    const relatedLaurels = projectName && laurels
                        ? laurels.filter(laurel => laurel?.context?.custom?.projects.includes(projectName))
                        : [];
                    return {...image, laurels: relatedLaurels.length > 0 ? relatedLaurels : undefined};
                })
                : images;

            return [section.key, withLaurels.sort((a, b) => sortByOrderMetaData(a, b))];
        }));

        setGalleries(Object.fromEntries(loadedSections));
    }, [imagesCache, laurels, setImagesCache]);

    useEffect(() => {
        getData();
    }, [getData]);

    return (
        <div className="home">
            <IntroVideo />
            {sections.map(section => {
                const gallery = galleries[section.key] || [];

                return (
                    <section id={section.key} className={`home-section ${section.key}`} key={section.key}>
                        <h1 ref={section.key === sections[0].key ? firstHeadingRef : undefined}>{section.label}</h1>
                        <Gallery
                            images={gallery}
                            isProjectCover={true}
                            route={section.key}
                            animated
                            initialItems={3}
                            loadMoreCount={3}
                        />
                    </section>
                );
            })}
            <section className="home-section bio-contact">
                <div id="bio"><Bio /></div>
                <div id="contact"><Contact /></div>
            </section>
        </div>
    );
}

export default Home;
